import { DataTable } from '@app/components/data-table/data-table';
import { useLocalDataTable } from '@app/components/data-table/hooks/use-local-data-table';
import { ProductPackagingType } from '@shared/api/types';
import { Button } from '@shared/components/ui/button';
import { useState } from 'react';
import ProductDetailPackagingSheet from './product-detail-packaging-sheet';
import { useProductPackagingTableColumns } from './product-packaging-table-columns';

const PAGE_SIZE = 10;

function PackagingDataTable({
  data,
  maxEmissions,
  onRowClick,
}: {
  data: ProductPackagingType[];
  maxEmissions: number;
  onRowClick: (row: ProductPackagingType | undefined) => void;
}) {
  const columns = useProductPackagingTableColumns(maxEmissions);
  const { table } = useLocalDataTable({
    data,
    columns,
    getRowId: (row: ProductPackagingType) => row.name,
    initialState: { sorting: [{ id: 'emissions', desc: true }] },
  });
  return (
    <DataTable
      table={table}
      pagination={false}
      className="bg-gray-00 text-left shadow-sm"
      tableClassName="table-fixed"
      columnClassName={{ emissions: 'border-r', bar: 'pl-0 mr-0' }}
      onRowClick={(row) => onRowClick(row?.original)}
      rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
    />
  );
}

interface ProductPackagingTableProps {
  packagingTypes: ProductPackagingType[];
  maxEmissions: number;
  totalEmissions?: number;
  product?: string;
}

export default function ProductPackagingTable({
  packagingTypes,
  maxEmissions,
  totalEmissions,
  product,
}: ProductPackagingTableProps) {
  const [showAll, setShowAll] = useState(false);
  const [selectedPackagingType, setSelectedPackagingType] =
    useState<ProductPackagingType>();

  const visibleTypes = showAll
    ? packagingTypes
    : packagingTypes.slice(0, PAGE_SIZE);

  return (
    <div>
      <PackagingDataTable
        key={visibleTypes.length}
        data={visibleTypes}
        maxEmissions={maxEmissions}
        onRowClick={setSelectedPackagingType}
      />
      {packagingTypes.length > PAGE_SIZE && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? 'Show less'
            : `Show ${packagingTypes.length - PAGE_SIZE} more`}
        </Button>
      )}
      {selectedPackagingType && totalEmissions != null && product && (
        <ProductDetailPackagingSheet
          product={product}
          isOpen={!!selectedPackagingType}
          setIsOpen={(open) => {
            if (!open) setSelectedPackagingType(undefined);
          }}
          packagingType={selectedPackagingType}
          totalEmissions={totalEmissions}
        />
      )}
    </div>
  );
}
